import { DeleteParams, GetListParams } from 'react-admin';
import { CustomDataProvider, registerModuleDataProvider } from '../../../providers/CustomDataProvider';
import { baseUrl, httpClient } from '../../../providers/AppDataProvider';
import { Resources } from '../../resource';
import type { CoinImageLookupResult } from '../../../services/coinImagesService';

function buildListQuery(params: GetListParams): URLSearchParams {
  const { page = 1, perPage = 10 } = params.pagination || {};
  const { filter = {} } = params;
  const qs = new URLSearchParams();
  qs.set('page', String(page));
  qs.set('size', String(perPage));

  const filterObj: Record<string, string> = {};
  const filename = String(filter.filename ?? '').trim();
  const symbol = String(filter.symbol ?? '').trim();
  if (filename) filterObj.filename = filename;
  if (symbol) filterObj.symbol = symbol;
  if (Object.keys(filterObj).length > 0) {
    qs.set('filter', JSON.stringify(filterObj));
  }

  if (params.sort?.field) {
    qs.set('sort', JSON.stringify({ field: params.sort.field, order: params.sort.order }));
  }

  return qs;
}

function mapLookupToRows(payload: CoinImageLookupResult) {
  return payload.variants.map((v) => ({
    id: v.file?.id ?? `${payload.symbol}__${v.extension}`,
    symbol: payload.symbol,
    extension: v.extension,
    filename: v.filename,
    uploaded: v.uploaded,
    importSourceUrl: v.importSourceUrl,
    cdnURL: v.file?.cdnURL,
    publicURL: v.file?.publicURL,
    uploadedAt: v.file?.uploadedAt,
    size: v.file?.size,
    _lookupMode: true,
  }));
}

class CoinImagesDataProvider implements CustomDataProvider {
  // eslint-disable-next-line class-methods-use-this
  async getList(resource: string, params: GetListParams): Promise<any> {
    if (resource !== Resources.coinImages) {
      return Promise.reject(new Error('Unsupported getList resource'));
    }

    const qs = buildListQuery(params);
    const { json } = await httpClient(`${baseUrl}/cdn/coin-images?${qs.toString()}`);

    // Symbol lookup: { symbol, variants, publicUrlPrefix }
    const lookup = (json?.variants ? json : json?.data?.variants ? json.data : null) as
      | CoinImageLookupResult
      | null;
    if (lookup?.variants && lookup?.symbol) {
      const data = mapLookupToRows(lookup);
      return { data, total: data.length };
    }

    // Paginated list: { data: File[], total, page, size } — do NOT treat json.data as the whole payload
    const rows = (Array.isArray(json?.data) ? json.data : []) as Record<string, unknown>[];
    const total = typeof json?.total === 'number' ? json.total : rows.length;
    const data = rows.map((r, i) =>
      r?.id != null ? r : { ...r, id: r.filename ?? `row-${i}` }
    );
    return { data, total };
  }

  // eslint-disable-next-line class-methods-use-this
  async delete(resource: string, params: DeleteParams): Promise<any> {
    if (resource !== Resources.coinImages) {
      return Promise.reject(new Error('Unsupported delete resource'));
    }
    const id = String(params.id);
    await httpClient(`${baseUrl}/cdn/coin-images/${id}`, { method: 'DELETE' });
    return { data: { id: params.id } };
  }
}

export const dataProvider = new CoinImagesDataProvider();

registerModuleDataProvider(Resources.coinImages, dataProvider);
